<div class="parking-right-edit-dialog">
    <h2 mat-dialog-title class="flex items-center gap-2">
        <mat-icon class="text-blue-600">edit</mat-icon>
        Edit Parking Right
    </h2>

    <mat-dialog-content>

        <!-- ==================== LONG-TERM PATH ==================== -->
        @if (isLongTerm) {
        @if (isLoadingLongTermTypes()) {
        <div class="flex items-center justify-center py-8">
            <mat-spinner diameter="40"></mat-spinner>
        </div>
        } @else {
        <form [formGroup]="form" class="space-y-4">

            <!-- Name -->
            <mat-form-field appearance="fill" class="w-full">
                <mat-label>Name</mat-label>
                <input matInput formControlName="name" placeholder="e.g. Acme GmbH">
                <mat-hint>Company or person name (optional)</mat-hint>
            </mat-form-field>

            <!-- License Plate -->
            <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">License Plates</label>
                <!-- Always available: a right may authorise several vehicles. -->
                <div class="flex gap-2 items-start">
                    <mat-form-field appearance="fill" class="flex-1">
                        <mat-label>License Plate Number</mat-label>
                        <input matInput formControlName="licensePlateInput" placeholder="Enter license plate"
                            (keyup.enter)="addLicensePlate()" [disabled]="isAddingPlate()">
                    </mat-form-field>
                    <button mat-raised-button color="primary" type="button" (click)="addLicensePlate()"
                        [disabled]="isAddingPlate()" class="mt-1" aria-label="Add license plate">
                        @if (isAddingPlate()) {
                        <mat-spinner diameter="20"></mat-spinner>
                        } @else {
                        <ng-container><mat-icon>add</mat-icon> Add</ng-container>
                        }
                    </button>
                </div>
                @if (licensePlates().length > 0) {
                <div class="bg-gray-50 rounded-lg p-3">
                    <div class="text-sm text-gray-600 mb-2">License Plates:</div>
                    <mat-chip-set>
                        @for (plate of licensePlates(); track plate.id) {
                        <mat-chip (removed)="removeLicensePlate(plate)">
                            {{ plate.fullNumber }}
                            <button matChipRemove aria-label="Remove license plate {{ plate.fullNumber }}">
                                <mat-icon>cancel</mat-icon>
                            </button>
                        </mat-chip>
                        }
                    </mat-chip-set>
                </div>
                }
                @if (licensePlates().length === 0) {
                <div class="text-sm text-orange-600 flex items-center gap-1">
                    <mat-icon class="text-base">warning</mat-icon>
                    License plate is required
                </div>
                }
            </div>

            <!-- Zones -->
            <mat-form-field appearance="fill" class="w-full">
                <mat-label>Parking Zones</mat-label>
                <mat-select formControlName="selectedZones" multiple>
                    @for (zone of availableZones; track zone.id) {
                    <mat-option [value]="zone.id">
                        <div class="flex items-center gap-2">
                            <mat-icon class="text-base text-blue-600">location_on</mat-icon>
                            <span>{{ zone.name }}</span>
                        </div>
                    </mat-option>
                    }
                </mat-select>
                <mat-hint>Leave empty to allow all zones</mat-hint>
            </mat-form-field>

            <!-- Simplified Validity -->
            <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">Validity</label>
                <mat-radio-group aria-label="Validity" class="flex flex-col gap-2"
                    [value]="form.get('validToEnabled')?.value ? 'until' : 'always'"
                    (change)="form.get('validToEnabled')?.setValue($event.value === 'until')">
                    <mat-radio-button value="always">Always valid</mat-radio-button>
                    <mat-radio-button value="until">Valid until a specific date</mat-radio-button>
                </mat-radio-group>
                @if (form.get('validToEnabled')?.value) {
                <mat-form-field appearance="fill" class="w-full">
                    <mat-label>Valid Until</mat-label>
                    <input matInput [matDatepicker]="ltValidToPicker" formControlName="validTo">
                    <mat-datepicker-toggle matIconSuffix [for]="ltValidToPicker"></mat-datepicker-toggle>
                    <mat-datepicker #ltValidToPicker></mat-datepicker>
                </mat-form-field>
                }
            </div>

        </form>
        }
        } @else {
        <!-- ==================== ASSIGNED ZONE PARKERS PATH ==================== -->
        @if (isLoadingTypes()) {
        <div class="flex items-center justify-center py-8">
            <mat-spinner diameter="40"></mat-spinner>
        </div>
        } @else {
        <form [formGroup]="form" class="space-y-4">
            <!-- Parking Type Selection -->
            <mat-form-field appearance="fill" class="w-full">
                <mat-label>Assigned Zone Parking Type</mat-label>
                <mat-select formControlName="parkingTypeId" required>
                    @for (type of assignedZoneTypes(); track type.id) {
                    <mat-option [value]="type.id">
                        <div class="flex items-center gap-2">
                            <mat-icon class="text-base text-purple-600">person_pin</mat-icon>
                            <span>{{ type.parkingType?.type?.name || 'Assigned Zone Type' }}</span>
                        </div>
                    </mat-option>
                    }
                </mat-select>
                @if (form.get('parkingTypeId')?.hasError('required') && form.get('parkingTypeId')?.touched) {
                <mat-error>Please select a parking type</mat-error>
                }
            </mat-form-field>

            <!-- Parking Zones Selection -->
            <mat-form-field appearance="fill" class="w-full">
                <mat-label>Parking Zones</mat-label>
                <mat-select formControlName="selectedZones" multiple>
                    @for (zone of availableZones; track zone.id) {
                    <mat-option [value]="zone.id">
                        <div class="flex items-center gap-2">
                            <mat-icon class="text-base text-blue-600">location_on</mat-icon>
                            <span>{{ zone.name }}</span>
                        </div>
                    </mat-option>
                    }
                </mat-select>
                @if (availableZones.length === 0) {
                <mat-hint class="text-orange-600">No zones available for this parking space</mat-hint>
                }
            </mat-form-field>

            <!-- Right Type Selection -->
            <mat-form-field appearance="fill" class="w-full">
                <mat-label>Right Type</mat-label>
                <mat-select formControlName="rightType" required>
                    @for (option of rightTypeOptions; track option.value) {
                    <mat-option [value]="option.value">{{ option.label }}</mat-option>
                    }
                </mat-select>
            </mat-form-field>

            <!-- Validity Period -->
            <div class="grid grid-cols-2 gap-4">
                <mat-form-field appearance="fill">
                    <mat-label>Valid From</mat-label>
                    <input matInput [matDatepicker]="validFromPicker" formControlName="validFrom">
                    <mat-datepicker-toggle matIconSuffix [for]="validFromPicker"></mat-datepicker-toggle>
                    <mat-datepicker #validFromPicker></mat-datepicker>
                    <mat-hint>Optional start date</mat-hint>
                </mat-form-field>
                <mat-form-field appearance="fill">
                    <mat-label>Valid To</mat-label>
                    <input matInput [matDatepicker]="validToPicker" formControlName="validTo">
                    <mat-datepicker-toggle matIconSuffix [for]="validToPicker"></mat-datepicker-toggle>
                    <mat-datepicker #validToPicker></mat-datepicker>
                    <mat-hint>Optional end date</mat-hint>
                </mat-form-field>
            </div>

            <!-- Time-Based Specific Fields -->
            @if (isTimeBased) {
            <div class="border rounded-lg p-4 bg-blue-50 space-y-4">
                <div class="flex items-center gap-2 text-blue-700 font-medium">
                    <mat-icon>schedule</mat-icon>
                    <span>Time-Based Settings</span>
                </div>

                <mat-checkbox formControlName="isAllDay" color="primary">All Day Access</mat-checkbox>

                @if (!form.get('isAllDay')?.value) {
                <div class="grid grid-cols-2 gap-4">
                    <mat-form-field appearance="fill">
                        <mat-label>Start Time</mat-label>
                        <input matInput type="time" formControlName="startTime">
                    </mat-form-field>
                    <mat-form-field appearance="fill">
                        <mat-label>End Time</mat-label>
                        <input matInput type="time" formControlName="endTime">
                    </mat-form-field>
                </div>
                }

                <mat-form-field appearance="fill" class="w-full">
                    <mat-label>Valid Days</mat-label>
                    <mat-select formControlName="validDays" multiple>
                        @for (day of daysOfWeek; track day.value) {
                        <mat-option [value]="day.value">{{ day.label }}</mat-option>
                        }
                    </mat-select>
                    <mat-hint>Leave empty for all days</mat-hint>
                </mat-form-field>

                <mat-checkbox formControlName="isRecurring" color="primary">Recurring (repeats weekly)</mat-checkbox>
            </div>
            }

            <!-- Duration-Based Specific Fields -->
            @if (isDurationBased) {
            <div class="border rounded-lg p-4 bg-green-50 space-y-4">
                <div class="flex items-center gap-2 text-green-700 font-medium">
                    <mat-icon>timer</mat-icon>
                    <span>Duration-Based Settings</span>
                </div>

                <mat-form-field appearance="fill" class="w-full">
                    <mat-label>Duration (minutes)</mat-label>
                    <input matInput type="number" formControlName="durationInMinutes" min="1">
                    <mat-hint>Parking duration in minutes</mat-hint>
                </mat-form-field>

                <mat-checkbox formControlName="isRenewable" color="primary">Allow Renewal</mat-checkbox>

                @if (form.get('isRenewable')?.value) {
                <mat-form-field appearance="fill" class="w-full">
                    <mat-label>Max Renewals</mat-label>
                    <input matInput type="number" formControlName="maxRenewals" min="1">
                    <mat-hint>Leave empty for unlimited</mat-hint>
                </mat-form-field>
                }
            </div>
            }

            <!-- License Plate Input -->
            <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">License Plates</label>
                <!-- Always available: a right may authorise several vehicles. -->
                <div class="flex gap-2 items-start">
                    <mat-form-field appearance="fill" class="flex-1">
                        <mat-label>License Plate Number</mat-label>
                        <input matInput formControlName="licensePlateInput" placeholder="Enter license plate"
                            (keyup.enter)="addLicensePlate()" [disabled]="isAddingPlate()">
                    </mat-form-field>
                    <button mat-raised-button color="primary" type="button" (click)="addLicensePlate()"
                        [disabled]="isAddingPlate()" class="mt-1" aria-label="Add license plate">
                        @if (isAddingPlate()) {
                        <mat-spinner diameter="20"></mat-spinner>
                        } @else {
                        <ng-container><mat-icon>add</mat-icon> Add</ng-container>
                        }
                    </button>
                </div>
                @if (licensePlates().length > 0) {
                <div class="bg-gray-50 rounded-lg p-3">
                    <div class="text-sm text-gray-600 mb-2">License Plates:</div>
                    <mat-chip-set>
                        @for (plate of licensePlates(); track plate.id) {
                        <mat-chip (removed)="removeLicensePlate(plate)">
                            {{ plate.fullNumber }}
                            <button matChipRemove aria-label="Remove license plate {{ plate.fullNumber }}">
                                <mat-icon>cancel</mat-icon>
                            </button>
                        </mat-chip>
                        }
                    </mat-chip-set>
                </div>
                }
                @if (licensePlates().length === 0) {
                <div class="text-sm text-orange-600 flex items-center gap-1">
                    <mat-icon class="text-base">warning</mat-icon>
                    License plate is required
                </div>
                }
            </div>

            <!-- Comment (generic — available for all right kinds) -->
            <mat-form-field appearance="fill" class="w-full">
                <mat-label>Comment (optional)</mat-label>
                <textarea matInput formControlName="comment" rows="2"
                    placeholder="Notes, reference numbers, etc."></textarea>
            </mat-form-field>
        </form>
        }
        }
    </mat-dialog-content>

    <mat-dialog-actions align="end" class="gap-2">
        <jjames-button [variant]="'secondary'" (clicked)="onCancel()">
            Cancel
        </jjames-button>
        <jjames-button [variant]="'primary'" (clicked)="onSubmit()"
            [disabled]="isLoading() || licensePlates().length === 0" [loading]="isLoading()" matIconLeft="save">
            Save Changes
        </jjames-button>
    </mat-dialog-actions>
</div>
